import { sortBy, upperFirst } from "lodash-es"
import { useRef } from "react"

import type { CompanyRunResearch } from "@/admin/monitoring/company-run-research-data"
import {
  checkedMarks,
  dataByResearchMark,
  ResearchMark,
} from "@/admin/monitoring/research-mark"
import {
  ResearchMarkLegend,
  ResearchMarkSquare,
} from "@/admin/monitoring/research-source-table"
import {
  dataByResearchCategory,
  researchCategories,
  type ResearchCategory,
} from "@/constants/terminal/research-category"
import { dataByResearchInsufficiencyReason } from "@/constants/terminal/research-insufficiency-reason"
import { ResearchSufficiencyOutcome } from "@/constants/terminal/research-sufficiency-outcome"
import { cn } from "@/lib/utils"

export type ResearchBand = CompanyRunResearch["bands"][number]
type Cell = ResearchBand["cells"][number]

const liveMarks = new Set([ResearchMark.RUNNING, ResearchMark.QUEUED])
// A disclosure is not a search anyone could have run.
const disclosureMarks = new Set([
  ResearchMark.UNCAPTURED,
  ResearchMark.NO_SOURCE,
])

export const isLiveCell = (cell: Cell) => liveMarks.has(cell.mark)
// A search with no usable answer yet that someone could still run.
export const isOpenCell = (cell: Cell) =>
  !checkedMarks.has(cell.mark) &&
  !liveMarks.has(cell.mark) &&
  !disclosureMarks.has(cell.mark)

// A category's verdict with the counts it rests on, so "Sufficient" never sits
// silently beside a failed search: Research Sufficiency weighs only required
// searches, and a report-only category is sufficient by policy (R9).
export const bandSummary = (band: ResearchBand | undefined) => {
  const cells = band?.cells ?? []
  const weighed = cells.filter((cell) => cell.isWeighed)
  // A Source with no capture is disclosed, not a search anyone could run, so
  // it never sits in the optional count.
  const optional = cells.filter(
    (cell) => !cell.isWeighed && !disclosureMarks.has(cell.mark)
  )
  const checkedCount = weighed.filter((cell) =>
    checkedMarks.has(cell.mark)
  ).length
  const liveCount = cells.filter(isLiveCell).length
  const base = {
    liveCount,
    checkedCount,
    weighedCount: weighed.length,
    optionalCount: optional.length,
    optionalCheckedCount: optional.filter((cell) => checkedMarks.has(cell.mark))
      .length,
  }
  if (!band?.outcome)
    return {
      ...base,
      label: "Sufficiency not recorded",
      detail: null,
      tone: "quiet" as const,
    }
  if (band.isReportOnly) {
    const unsearchedCount = cells.filter(isOpenCell).length
    return {
      ...base,
      label: "Report only",
      detail:
        unsearchedCount > 0
          ? `${unsearchedCount} not searched. Disclosed on the File; never blocks it.`
          : "Disclosed on the File; never blocks it.",
      tone: unsearchedCount > 0 ? ("caution" as const) : ("quiet" as const),
    }
  }
  // The Required and Optional meters already say what a sufficient verdict
  // rests on.
  if (band.outcome === ResearchSufficiencyOutcome.SUFFICIENT)
    return {
      ...base,
      label: "Sufficient",
      detail: null,
      tone: "quiet" as const,
    }
  const reasons = band.reasons
    .map((reason) => dataByResearchInsufficiencyReason[reason].label)
    .join(", ")
    .toLowerCase()
  const open = weighed.filter(isOpenCell).length
  return {
    ...base,
    label: "Insufficient",
    detail:
      open > 0
        ? `${open} required Research ${open === 1 ? "Task has" : "Tasks have"} no usable Source Check${reasons ? ` (${reasons})` : ""}.`
        : reasons && `${upperFirst(reasons)}.`,
    tone: "bad" as const,
  }
}

export const toneClassName = {
  quiet: "text-muted-foreground",
  caution: "text-caution-foreground",
  bad: "text-bad-foreground",
}

// One square per search, worst first, so a category's failures lead. A search
// Research Sufficiency does not weigh is dimmed: it never moves the verdict.
const MarkStrip = ({ band }: { band: ResearchBand | undefined }) => {
  const cells = sortBy(
    band?.cells ?? [],
    (cell) => (cell.isWeighed ? 0 : 1),
    (cell) => dataByResearchMark[cell.mark].severity
  )
  if (!cells.length) return null
  return (
    <span className="flex flex-wrap gap-0.5">
      {cells.map((cell, index) => (
        <ResearchMarkSquare
          key={index}
          mark={cell.mark}
          className={cn(!cell.isWeighed && "opacity-40")}
        />
      ))}
    </span>
  )
}

export const ResearchCategoryRail = ({
  bandByCategory,
  selectedCategory,
  onSelectCategory,
}: {
  bandByCategory: Partial<Record<ResearchCategory, ResearchBand>>
  selectedCategory: ResearchCategory
  onSelectCategory: (category: ResearchCategory) => void
}) => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])
  // Up and down move through categories the way a list box does.
  const moveSelection = (index: number) => {
    const next = researchCategories[index]
    if (!next) return
    onSelectCategory(next)
    buttonRefs.current[index]?.focus()
  }
  const allCells = Object.values(bandByCategory).flatMap((band) => band.cells)
  return (
    <nav
      aria-label="Research categories"
      className="hidden space-y-4 lg:sticky lg:top-4 lg:block lg:self-start"
    >
      <ul className="space-y-px">
        {researchCategories.map((category, index) => {
          const band = bandByCategory[category]
          const summary = bandSummary(band)
          const isSelected = category === selectedCategory
          const isSettled = summary.tone === "quiet" && summary.liveCount === 0
          return (
            <li key={category}>
              <button
                ref={(element) => {
                  buttonRefs.current[index] = element
                }}
                aria-current={isSelected ? "true" : undefined}
                onClick={() => onSelectCategory(category)}
                onKeyDown={(event) => {
                  if (event.key === "ArrowDown") moveSelection(index + 1)
                  else if (event.key === "ArrowUp") moveSelection(index - 1)
                  else return
                  event.preventDefault()
                }}
                className={cn(
                  "focus-visible:ring-terminal-accent hover:bg-surface-hover w-full rounded-sm px-3 py-2.5 text-left focus-visible:ring-1 focus-visible:outline-none",
                  isSelected && "ring-foreground/60 ring-1 ring-inset"
                )}
              >
                <span
                  className={cn(
                    "block text-sm",
                    isSettled && !isSelected && "text-muted-foreground"
                  )}
                >
                  {dataByResearchCategory[category].name}
                </span>
                <span className="mt-2 flex items-center justify-between gap-3 text-xs">
                  <MarkStrip band={band} />
                  <span
                    className={cn(
                      "shrink-0 tabular-nums",
                      summary.liveCount > 0
                        ? "text-terminal-accent"
                        : toneClassName[summary.tone]
                    )}
                  >
                    {summary.liveCount > 0
                      ? `${summary.liveCount} in progress`
                      : summary.label}
                    {summary.liveCount === 0 &&
                      !band?.isReportOnly &&
                      summary.weighedCount > 0 &&
                      ` ${summary.checkedCount}/${summary.weighedCount}`}
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      <ResearchMarkLegend
        marks={allCells.map((cell) => cell.mark)}
        className="flex-col px-3"
      >
        {allCells.some((cell) => !cell.isWeighed) && (
          <li className="flex items-center gap-1.5">
            <ResearchMarkSquare
              mark={ResearchMark.CHECKED}
              className="opacity-40"
            />
            Faded: optional, doesn’t affect sufficiency
          </li>
        )}
      </ResearchMarkLegend>
    </nav>
  )
}
